import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../providers/auth-provider';
import { ModuleRecoveryGate } from './module-recovery-gate';
import { useUnsavedActions } from '../../general/unsaved-changes-context';
import { Location as RouterLocation, NavigateOptions, useLocation, useNavigate } from "react-router-dom";
import { ContentPage } from '../content-page';
import { FlowCanvas } from './editor-flow-canvas';
import { FragmentInspector } from './editor-fragment-inspector';
import { TopBar } from './editor-toolbar';
import { ModuleDto } from '../../general/dtos';
import { ManagedModuleEditorProvider } from '../../providers/managed-module-management-provider';
import { GeneralPropsPanel } from './general-props-panel';
import { useBookmarkSelection } from '../../general/bookmark-selection';
import { useApi } from '../../providers/api-provider';

export default function ManagedModuleEditorPage() {
  const location = useLocation();
  const owner = useContext(AuthContext)?.getUser()?.sub;
  const { withoutWarning } = useUnsavedActions();
  const navigate = useNavigate();
  const returnTo: RouterLocation | undefined = location.state?.returnTo;
  const fullModule: ModuleDto = location.state?.fullModule;
  const stateModuleKey = fullModule?.module_key;
  const stateModuleRef = useRef(fullModule);
  stateModuleRef.current = fullModule;
  const [moduleKey] = useBookmarkSelection('module');
  const api = useApi();
  const [loadError, setLoadError] = useState('');

  const [initialModule, setInitialModule] = useState<ModuleDto | null>(null);

  useEffect(() => {
    let active = true;
    setInitialModule(null);
    setLoadError('');
    if (moduleKey) {
        if (!api) return;
        api.getModule(moduleKey).then(module => {
            if (!active) return;
            if (!module || !module.is_managed) throw new Error('Managed module not found.');
            setInitialModule(module);
        }).catch(error => { if (active) setLoadError(error?.response?.data?.message || error?.message || 'Could not load this managed module.'); });
    } else if (stateModuleRef.current) {
        setInitialModule(stateModuleRef.current);
    } else {
        const newModule: ModuleDto = {
            module_key: "",
            folder_id: location.state?.folderId ?? null,
            name: "New Managed Module",
            description: "",
            created_at: "",
            updated_at: "",
            content: "",
            instructions: "",
            theme: [],
            prerequisites: [],
            fragments: [],
            finalQuizFragmentKey: null,
        };
        setInitialModule(newModule);
    }
    return () => { active = false; };
    // Fragment bookmark navigation clones history state. Reload only when the
    // module identity changes, preserving the editor's unsaved draft.
  }, [stateModuleKey, moduleKey, api]);

  const onCancel = async (): Promise<void> => {
    if (returnTo) {
        withoutWarning(() => navigate(returnTo.pathname + returnTo.search + returnTo.hash, { state: returnTo.state }));
    } else {
        const params = new URLSearchParams();
        if (initialModule?.module_key) params.set('module', initialModule.module_key);
        if (initialModule?.folder_id != null) params.set('folder', String(initialModule.folder_id));
        const search = params.toString();
        withoutWarning(() => navigate(`/admin/modules${search ? `?${search}` : ''}`));
    }
  }

  const onSave = async (module: ModuleDto): Promise<void> => {
    const options: NavigateOptions = { state: { ...returnTo?.state, module: module }, replace: true };
    if (returnTo) {
        navigate(returnTo.pathname + returnTo.search + returnTo.hash, options);
    } else {
        navigate(`/admin/modules?module=${encodeURIComponent(module.module_key)}`, options);
    }
  }

  if (loadError) return <p role="alert">{loadError}</p>;
  if (!initialModule) return <div>Loading…</div>;

    return (
        <div style={{ flex: 1, display: 'flex', minHeight: 0, width: '100%', minWidth: '0'/*, backgroundColor: 'blue'*/ }}>
            <ModuleRecoveryGate key={JSON.stringify([owner, initialModule.module_key])} owner={owner} module={initialModule}>
            {(value, session) => <ManagedModuleEditorProvider initialModule={value} baselineModule={initialModule} recoverySession={session}>
                <ContentPage
                    pageId="managed-module-creator"
                    pageTitle="Managed module editor"
                    leftPanelTitle="Fragments and actions"
                    rightPanelTitle="Fragment content"
                    leftContent={<>
                        <TopBar onCancel={onCancel} onSave={onSave}/>
                        <GeneralPropsPanel />
                        <FlowCanvas />
                        </>}
                    rightContent={<FragmentInspector />}
                />
            </ManagedModuleEditorProvider>}
            </ModuleRecoveryGate>
        </div>
    );
}
